/**
 * ローカル Zip 正本から使用簿 PDF／帳票一覧用データを組み立てる。
 * クラウド側 `reportService.getUsageLedgerData` と同じ数量計算の考え方。
 */
import { sampleLocalStore } from './sampleLocalStore';
import type { SampleLocalSample, SampleLocalUsageRecord } from './sampleLocalDb';

const CONSUMING = new Set(['使用', '廃棄']);
const CREATED = 'サンプル新規登録';

export type LocalUsageLedgerRecord = {
  id: number;
  usageDate: string;
  action: string | null;
  purpose: string | null;
  quantity: number;
  remainingQuantity: number | null;
  notes: string | null;
  recordedBy: string | null;
  user: null;
};

export type LocalUsageLedgerSample = {
  id: number;
  name: string;
  sampleType: string | null;
  bslLevel: string | null;
  initialQuantity: number | null;
  unit: string | null;
  storageDate: string | null;
  notes: string | null;
  pathogenGenus: string | null;
  pathogenSpecies: string | null;
  pathogenSubspecies: string | null;
  pathogenStrain: string | null;
  drawerNumber: number | null;
  caseName: string | null;
  createdAt: string;
  storageRoom: { name: string; location: string | null } | null;
  creatorName: string | null;
  periodStart: string;
  periodEnd: string;
  startQuantity: number;
  usedQuantity: number;
  endQuantity: number;
  usageRecords: LocalUsageLedgerRecord[];
};

function parseDay(iso: string): Date {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function matchesKeyword(sample: SampleLocalSample, roomName: string, roomLoc: string, q: string): boolean {
  if (!q) return true;
  const hay = [
    sample.name,
    sample.sampleType,
    sample.notes,
    sample.pathogenGenus,
    sample.pathogenSpecies,
    sample.pathogenSubspecies,
    sample.pathogenStrain,
    sample.creatorDisplayName,
    sample.caseName,
    sample.bslLevel,
    roomName,
    roomLoc,
    sample.drawerNumber != null ? String(sample.drawerNumber) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function filterSample(
  sample: SampleLocalSample,
  roomName: string,
  roomLoc: string,
  filters?: {
    keyword?: string;
    storageRoom?: string;
    drawerNumber?: string;
    caseName?: string;
    bslLevel?: string;
    sampleType?: string;
    sampleName?: string;
    location?: string;
    pathogenGenus?: string;
    pathogenSpecies?: string;
    pathogenSubspecies?: string;
    pathogenStrain?: string;
  }
): boolean {
  if (!filters) return true;
  const q = (filters.keyword || '').trim().toLowerCase();
  if (q && !matchesKeyword(sample, roomName, roomLoc, q)) return false;
  if (filters.sampleName && !(sample.name || '').includes(filters.sampleName)) return false;
  if (filters.sampleType && !(sample.sampleType || '').includes(filters.sampleType)) return false;
  if (filters.bslLevel && sample.bslLevel !== filters.bslLevel) return false;
  if (filters.caseName && !(sample.caseName || '').includes(filters.caseName)) return false;
  if (filters.drawerNumber != null && filters.drawerNumber !== '') {
    const n = parseInt(filters.drawerNumber, 10);
    if (!Number.isNaN(n) && sample.drawerNumber !== n) return false;
  }
  if (filters.storageRoom) {
    const needle = filters.storageRoom;
    if (!roomName.includes(needle) && !roomLoc.includes(needle)) return false;
  }
  if (filters.location && !roomLoc.includes(filters.location)) return false;
  if (filters.pathogenGenus && !(sample.pathogenGenus || '').includes(filters.pathogenGenus)) {
    return false;
  }
  if (filters.pathogenSpecies && !(sample.pathogenSpecies || '').includes(filters.pathogenSpecies)) {
    return false;
  }
  if (
    filters.pathogenSubspecies &&
    !(sample.pathogenSubspecies || '').includes(filters.pathogenSubspecies)
  ) {
    return false;
  }
  if (filters.pathogenStrain && !(sample.pathogenStrain || '').includes(filters.pathogenStrain)) {
    return false;
  }
  return true;
}

function mapRecord(r: SampleLocalUsageRecord): LocalUsageLedgerRecord {
  return {
    id: r.id,
    usageDate: r.usageDate,
    action: r.action ?? null,
    purpose: r.purpose ?? null,
    quantity: r.quantity,
    remainingQuantity: r.remainingQuantity ?? null,
    notes: r.notes ?? null,
    recordedBy: r.recordedBy ?? null,
    user: null,
  };
}

export function buildLocalUsageLedgerData(params?: {
  baseDate?: string;
  filters?: Parameters<typeof filterSample>[3];
}): {
  baseDate: string;
  periodStart: string;
  samples: LocalUsageLedgerSample[];
} {
  const snap = sampleLocalStore.getSnapshot();
  const baseYmd = (params?.baseDate || ymd(new Date())).slice(0, 10);
  const baseDateEnd = new Date(`${baseYmd}T23:59:59.999`);
  const periodStart = new Date(baseDateEnd);
  periodStart.setFullYear(periodStart.getFullYear() - 1);
  periodStart.setHours(0, 0, 0, 0);
  const periodStartIso = periodStart.toISOString();
  const baseDateIso = baseDateEnd.toISOString();

  const roomMap = new Map(snap.storageRooms.map((r) => [r.id, r]));
  const usageBySample = new Map<number, SampleLocalUsageRecord[]>();
  for (const u of snap.usageRecords) {
    const list = usageBySample.get(u.sampleId) || [];
    list.push(u);
    usageBySample.set(u.sampleId, list);
  }

  const samples: LocalUsageLedgerSample[] = [];

  for (const s of snap.samples) {
    const createdAt = parseDay(s.createdAt);
    if (createdAt > baseDateEnd) continue;
    if (s.disposalDate) continue;

    const room = s.storageRoomId != null ? roomMap.get(s.storageRoomId) : undefined;
    const roomName = room?.name || '';
    const roomLoc = room?.location || '';
    if (!filterSample(s, roomName, roomLoc, params?.filters)) continue;

    const allUsage = (usageBySample.get(s.id) || []).slice().sort((a, b) => {
      const d = parseDay(a.usageDate).getTime() - parseDay(b.usageDate).getTime();
      if (d !== 0) return d;
      const aKey = a.action === CREATED ? 0 : 1;
      const bKey = b.action === CREATED ? 0 : 1;
      return aKey - bKey;
    });

    const creation = allUsage.find(
      (r) => r.action === CREATED && parseDay(r.usageDate) <= baseDateEnd
    );
    const initialQty = creation?.remainingQuantity ?? s.initialQuantity ?? 0;
    const usedBefore = allUsage
      .filter(
        (r) =>
          parseDay(r.usageDate) < periodStart &&
          r.action != null &&
          CONSUMING.has(r.action)
      )
      .reduce((sum, r) => sum + (r.quantity || 0), 0);

    const samplePeriodStart = createdAt > periodStart ? createdAt : periodStart;
    const isCreatedInPeriod = createdAt > periodStart;

    let records = allUsage.filter((r) => {
      const ud = parseDay(r.usageDate);
      if (ud > baseDateEnd) return false;
      if (r.action === CREATED && isCreatedInPeriod) return true;
      return ud >= samplePeriodStart;
    });

    if (isCreatedInPeriod && creation && !records.some((r) => r.action === CREATED)) {
      records = [creation, ...records].sort(
        (a, b) => parseDay(a.usageDate).getTime() - parseDay(b.usageDate).getTime()
      );
    }

    const startQty =
      createdAt <= periodStart ? Math.max(0, initialQty - usedBefore) : initialQty;
    const usedInPeriod = records
      .filter((r) => r.action != null && CONSUMING.has(r.action))
      .reduce((sum, r) => sum + (r.quantity || 0), 0);
    const endQty = Math.max(0, startQty - usedInPeriod);

    samples.push({
      id: s.id,
      name: s.name,
      sampleType: s.sampleType ?? null,
      bslLevel: s.bslLevel ?? null,
      initialQuantity: s.initialQuantity ?? null,
      unit: s.unit ?? null,
      storageDate: s.storageDate ?? null,
      notes: s.notes ?? null,
      pathogenGenus: s.pathogenGenus ?? null,
      pathogenSpecies: s.pathogenSpecies ?? null,
      pathogenSubspecies: s.pathogenSubspecies ?? null,
      pathogenStrain: s.pathogenStrain ?? null,
      drawerNumber: s.drawerNumber ?? null,
      caseName: s.caseName ?? null,
      createdAt: s.createdAt,
      storageRoom: room ? { name: room.name, location: room.location } : null,
      creatorName: s.creatorDisplayName || 'Local',
      periodStart: samplePeriodStart.toISOString(),
      periodEnd: baseDateIso,
      startQuantity: startQty,
      usedQuantity: usedInPeriod,
      endQuantity: endQty,
      usageRecords: records.map(mapRecord),
    });
  }

  samples.sort((a, b) => a.id - b.id);

  return {
    baseDate: baseDateIso,
    periodStart: periodStartIso,
    samples,
  };
}

/** 帳票 DataTables 用（1サンプル1行・基準日残量） */
export function buildLocalReportTablePage(params: {
  baseDate?: string;
  page?: number;
  limit?: number;
  stockFilter?: 'positive' | 'zero' | 'all';
  filters?: Parameters<typeof filterSample>[3];
}): {
  data: Array<{
    id: number;
    name: string;
    sampleType: string | null;
    bslLevel: string | null;
    remainingQuantity: number;
    usedQuantity: number;
    notes: string | null;
    hierarchy: {
      creator: string;
      storageRoom: string;
      storageRoomLocation: string | null;
      drawerNumber: number | null;
      caseName: string | null;
      bslLevel: string;
      pathogenGenus: string;
      pathogenSpecies: string;
      pathogenSubspecies: string;
      pathogenStrain: string;
      type: string;
      sampleName: string;
    };
  }>;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  statistics: {
    totalSamples: number;
    availableSamples: number;
    outOfStockSamples: number;
    byBslLevel: Array<{ level: string; count: number }>;
    bySampleType: Array<{ type: string; count: number }>;
    byFacility: Array<{ facility: string; storageRoom: string; count: number }>;
    monthlyRegistration: Array<{ month: string; count: number }>;
  };
} {
  const ledger = buildLocalUsageLedgerData({
    baseDate: params.baseDate,
    filters: params.filters,
  });
  const stock = params.stockFilter ?? 'positive';
  let rows = ledger.samples;
  if (stock === 'positive') rows = rows.filter((s) => s.endQuantity > 0);
  else if (stock === 'zero') rows = rows.filter((s) => s.endQuantity <= 0);

  const total = rows.length;
  const page = Math.max(1, params.page ?? 1);
  const limit = Math.max(1, Math.min(500, params.limit ?? 25));
  const start = (page - 1) * limit;
  const pageRows = rows.slice(start, start + limit);

  const availableSamples = ledger.samples.filter((s) => s.endQuantity > 0).length;
  const outOfStockSamples = ledger.samples.filter((s) => s.endQuantity <= 0).length;

  const byBsl = new Map<string, number>();
  const byType = new Map<string, number>();
  for (const s of ledger.samples) {
    const bsl = s.bslLevel || '-';
    const typ = s.sampleType || '-';
    byBsl.set(bsl, (byBsl.get(bsl) || 0) + 1);
    byType.set(typ, (byType.get(typ) || 0) + 1);
  }

  return {
    data: pageRows.map((s) => ({
      id: s.id,
      name: s.name,
      sampleType: s.sampleType,
      bslLevel: s.bslLevel,
      remainingQuantity: s.endQuantity,
      usedQuantity: s.usedQuantity,
      notes: s.notes,
      hierarchy: {
        creator: s.creatorName || 'Local',
        storageRoom: s.storageRoom?.name || '-',
        storageRoomLocation: s.storageRoom?.location ?? null,
        drawerNumber: s.drawerNumber,
        caseName: s.caseName,
        bslLevel: s.bslLevel || '-',
        pathogenGenus: s.pathogenGenus || '-',
        pathogenSpecies: s.pathogenSpecies || '-',
        pathogenSubspecies: s.pathogenSubspecies || '-',
        pathogenStrain: s.pathogenStrain || '-',
        type: s.sampleType || '-',
        sampleName: s.name,
      },
    })),
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    },
    statistics: {
      totalSamples: ledger.samples.length,
      availableSamples,
      outOfStockSamples,
      byBslLevel: [...byBsl.entries()].map(([level, count]) => ({ level, count })),
      bySampleType: [...byType.entries()].map(([type, count]) => ({ type, count })),
      byFacility: [],
      monthlyRegistration: [],
    },
  };
}
